/** Refresh ID tokens this many ms before exp (avoid edge clock skew). */
export const TOKEN_REFRESH_SKEW_MS = 5 * 60 * 1000;

/** Parse JWT exp claim (ms since epoch), or null if unavailable. */
export function jwtExpiresAtMs(token) {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/** True when token is missing or within skewMs of expiry. */
export function isJwtExpired(token, skewMs = TOKEN_REFRESH_SKEW_MS) {
  if (!token || typeof token !== 'string') return true;
  const exp = jwtExpiresAtMs(token);
  if (exp == null) return false;
  return exp <= Date.now() + skewMs;
}

export function extractRawIdToken(claims) {
  const raw = claims && typeof claims === 'object' && '__raw' in claims ? claims.__raw : undefined;
  return typeof raw === 'string' ? raw : null;
}

export function withTimeout(promise, ms, label = 'Operation') {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`${label} timed out`)), ms);
    }),
  ]);
}

const RECOVERY_FLAG = 'transformax_auth_recovery';

/** Redirect once to sign-in when silent refresh cannot renew the ID token. */
export function scheduleAuthRecovery() {
  if (typeof window === 'undefined') return;
  if (window.location.pathname.startsWith('/auth')) return;
  if (sessionStorage.getItem(RECOVERY_FLAG) === '1') return;
  sessionStorage.setItem(RECOVERY_FLAG, '1');
  const next = encodeURIComponent(`${window.location.pathname}${window.location.search || ''}`);
  window.location.assign(`/auth?mode=signin&next=${next}`);
}

export function clearAuthRecoveryFlag() {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(RECOVERY_FLAG);
}

/**
 * Resolve a fresh Auth0 ID token for Supabase third-party auth.
 * Uses cacheMode 'off' when stale — default caching refreshes API access tokens
 * but can leave an expired ID token in cache.
 */
export async function resolveSupabaseIdToken({
  getIdTokenClaims,
  getAccessTokenSilently,
  audience,
}) {
  const cached = extractRawIdToken(await getIdTokenClaims());
  if (cached && !isJwtExpired(cached)) return cached;

  const refreshOpts = {
    cacheMode: 'off',
    authorizationParams: audience ? { audience } : {},
  };

  try {
    await withTimeout(
      getAccessTokenSilently(refreshOpts),
      10_000,
      'Auth token refresh',
    );
  } catch (e) {
    const code = e && typeof e === 'object' && 'error' in e ? e.error : null;
    if (code === 'login_required' || code === 'consent_required') {
      scheduleAuthRecovery();
    }
    return null;
  }

  const fresh = extractRawIdToken(await getIdTokenClaims());
  if (fresh && !isJwtExpired(fresh, 0)) {
    clearAuthRecoveryFlag();
    return fresh;
  }

  scheduleAuthRecovery();
  return null;
}
